import { PATH_QUERY_MAP, PATH_TRANSLATIONS } from '@spa/shared/constants/url';
import { camelizeFromKebab } from '@spa/shared/utils/strings';
import { findValueAfterKey } from '../arrays';
import { filterNonNullishValues } from '../objects';

export const OLD_SEARCH_PARAMS = ['sw', 'ne'];

export const clean = key => object => {
  const { [key]: _, ...rest } = object;

  return rest;
};

export const cleanKey = clean('key');
export const cleanOrder = clean('order');
export const cleanMap = clean('view');
export const cleanCity = clean('city');
export const cleanPage = clean('page');
export const cleanUniversity = clean('university');
export const cleanNeighbourhood = clean('neighbourhood');
export const cleanStaticId = clean('staticId');
export const cleanRentType = clean('rent-type');
export const cleanPropertyFeatures = clean('property-features');
export const cleanRules = clean('rules');
export const cleanHousingType = clean('housing-type');
export const cleanEnsuite = clean('ensuite');
export const cleanOfferFeatures = clean('offer-features');
export const cleanBedType = clean('bed-type');

export const getCleanQuery = query => cleanNeighbourhood(cleanUniversity(cleanCity(query)));
export const fullCleaner = query => cleanPage(cleanStateAttributes(query));

export const isArrayNotEmpty = element => Array.isArray(element) && element.length;
export const notEmpty = element => !Array.isArray(element) && !!element;
export const exists = element => notEmpty(element) || isArrayNotEmpty(element);

const getValue = value => {
  if (isNaN(value) || !value) {
    return value;
  }

  if (value.toString().indexOf('.') !== -1) {
    return parseFloat(value);
  }

  return parseInt(value, 10);
};

const encodeParam = (key, element) => {
  if (isArrayNotEmpty(element)) {
    const cleanKey = key.replace('[]', '');

    return element.map(param => `${encodeURIComponent(cleanKey)}[]=${encodeURIComponent(param)}`).join('&');
  }

  return `${encodeURIComponent(key)}=${encodeURIComponent(element)}`;
};

export const objectToQueryParams = object =>
  Object.keys(object)
    .sort()
    .reduce((acc, key) => (exists(object[key]) ? [...acc, encodeParam(key, object[key])] : acc), [])
    .join('&');

const handleField = (key, field) => {
  const isFieldValidArray = isArrayNotEmpty(field);
  if (!isFieldValidArray && key.indexOf('[]') === -1) {
    return { [key]: getValue(field) };
  }

  const value = isFieldValidArray ? field : field.split(',');

  return { [key.replace('[]', '')]: value };
};

export const handlePathToQuery = cityParams => {
  if (!Array.isArray(cityParams)) {
    return {};
  }

  const filtersPath = findValueAfterKey(cityParams, 'f');
  if (!filtersPath) {
    return {};
  }

  return filtersPath.split(/[;,]/).reduce((acc, filter) => {
    const englishKey = findInMappings(filter, PATH_TRANSLATIONS);
    const category = findInMappings(englishKey, PATH_QUERY_MAP, false);
    const value = findInMappings(englishKey, PATH_QUERY_MAP);
    const currentCategoryValues = acc[category] || [];
    const isArray = PATH_QUERY_MAP[category].isArray;

    return englishKey && category
      ? {
          ...acc,
          [category]: isArray ? [...new Set([...currentCategoryValues, value])] : value
        }
      : acc;
  }, {});
};

const findInMappings = (filter, mappings, searchInValues = true) => {
  return Object.keys(mappings).reduce((acc, outerKey) => {
    if (acc) {
      return acc;
    }

    const innerMapping = mappings[outerKey].pathKeys ?? mappings[outerKey];
    const key = Object.entries(innerMapping).find(([key, value]) => {
      return searchInValues ? value === filter : innerMapping[key] === filter;
    })?.[0];

    return key && searchInValues ? key : key && !searchInValues ? outerKey : acc;
  }, null);
};

export const handleMultipleFields = fields =>
  Object.keys(fields).reduce(
    (acc, key) => ({
      ...acc,
      ...handleField(key, fields[key])
    }),
    {}
  );

export const cleanFields = fields => cleanMap(cleanOrder(cleanPage(cleanKey(handleMultipleFields(fields)))));

export const cleanStateAttributes = state =>
  Object.keys(getCleanQuery(state)).reduce(
    (acc, key) => (OLD_SEARCH_PARAMS.includes(key) || state[key] === null ? acc : { ...acc, [key]: state[key] }),
    {}
  );

export const getPageAsQuery = page => (!page || page === 1 ? {} : { page });

export const cleanStateQuery = (query, withStaticId) => {
  if (withStaticId) {
    return handleMultipleFields(cleanStateAttributes(query));
  }

  return cleanStaticId(handleMultipleFields(cleanStateAttributes(query)));
};

export const preparePageQuery = (query, withStaticId) => {
  const cleanedPageQuery = query.page === 1 ? cleanPage(query) : query;
  const queryString = `?${objectToQueryParams(
    cleanHousingType(
      cleanRentType(
        cleanPropertyFeatures(
          cleanRules(
            cleanStateQuery(cleanEnsuite(cleanOfferFeatures(cleanBedType(cleanKey(cleanedPageQuery)))), withStaticId)
          )
        )
      )
    )
  )}`;

  return queryString.length === 1 ? '' : queryString;
};

export const prepareStateQuery = query => `?${objectToQueryParams(handleMultipleFields(query))}`;

export const isReloadQueryFromMaps = (query, state) => query.view === 'map' && state.view !== null;

export const getMapQuery = (query, state) =>
  isReloadQueryFromMaps(query, state)
    ? { ne: query.ne || state.ne, sw: query.sw || state.sw, view: 'map' }
    : { ne: query.ne || state.ne, sw: query.sw || state.sw, view: null };

export const normalizeQuery = query =>
  Object.keys(query).reduce((acc, key) => ({ ...acc, [camelizeFromKebab(key)]: query[key] }), {});

export const changeUrlQuery = (router, queryParams) => {
  router.push(
    {
      pathname: router.pathname,
      query: { ...normalizeQuery(router.query), ...filterNonNullishValues(queryParams) }
    },
    undefined,
    { shallow: true }
  );
};

export const replaceFromQuery = (router, newQuery) => {
  const { pathname } = router;

  router.replace({ pathname, query: { ...normalizeQuery(filterNonNullishValues(newQuery)) } }, undefined, {
    shallow: true
  });
};
